import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  Chip,
  Icon,
  MenuItem,
  Snackbar,
} from "@mui/material";

import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function CampaignTerritoryCard({ campaignRef, triggerRefresh, territories, campaignTerritories }) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [openAttachTerritoryDialog, setOpenAttachTerritoryDialog] = useState(false);

  async function removeTerritory(ref) {
    setShowLoading(true);

    await axios
      .get(`/portal/campaigns/territories/${ref}/delete.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  async function attachTerritories() {
    if (selectedTerritories.length === 0) {
      setSeverity("error");
      setErrMsg("Select at least one territory");
      setOpen(true);

      return;
    }

    setOpenAttachTerritoryDialog(false);
    setShowLoading(true);

    const json = {
      campaign: campaignRef,
      territories: selectedTerritories,
    };

    await axios
      .post("portal/campaigns/territories/attach.json", json)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setSelectedTerritories([]);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");

        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );

        setOpen(true);
      });
  }

  const campaignTerritoriesTableColumns = [
    {
      Header: "Territory",
      accessor: "territory",
      align: "left",
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
    },
  ];

  const buildCampaignTerritoriesRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        territory: row.title,
        actions: (
          <MDBox mt={1} variant="button">
            <MDButton
              size="small"
              variant="outlined"
              color="error"
              iconOnly
              onClick={() => {
                if (window.confirm(`Remove territory?`) === true) {
                  removeTerritory(row.reference);
                }
              }}
            >
              <Icon
                sx={{
                  cursor: "pointer",
                }}
                fontSize="small"
                color="dark"
              >
                delete
              </Icon>
            </MDButton>
          </MDBox>
        ),
      });

      return null;
    });

    return rows;
  };

  /*
   * Only show territories that have not already
   * been attached to the campaign.
   */
  const attachedTerritoryReferences = campaignTerritories.map((row) => row.territory);
  const availableTerritories = territories.filter(
    (row) => !attachedTerritoryReferences.includes(row.reference)
  );

  useEffect(() => {
    const cTerritoriesRows = buildCampaignTerritoriesRows(campaignTerritories);

    setTableRows(cTerritoriesRows);
  }, [campaignTerritories]);

  return (
    <MDBox>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: "100%",
          }}
        >
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog open={openAttachTerritoryDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Attach Territories</MDBox>
        </DialogTitle>

        <DialogContent>
          <MDBox
            sx={{
              marginTop: "15px",
            }}
          >
            <MDBox mt={2}>
              <MDInput
                select
                fullWidth
                label="Territories"
                value={selectedTerritories}
                SelectProps={{
                  multiple: true,

                  style: {
                    padding: "12px 0px",
                  },

                  renderValue: (selected) => (
                    <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                      {selected.map((reference) => {
                        const territory = availableTerritories.find(
                          (item) => item.reference === reference
                        );

                        return (
                          <Chip
                            key={reference}
                            size="small"
                            label={territory ? territory.title : reference}
                          />
                        );
                      })}
                    </MDBox>
                  ),
                }}
                onChange={(event) => {
                  setSelectedTerritories(event.target.value);
                }}
              >
                {availableTerritories.map((record) => (
                  <MenuItem key={record.reference} value={record.reference}>
                    <Checkbox checked={selectedTerritories.includes(record.reference)} />

                    <MDTypography variant="body2">{record.title}</MDTypography>
                  </MenuItem>
                ))}
              </MDInput>

              {availableTerritories.length === 0 && (
                <MDTypography display="block" variant="caption" color="text" mt={1}>
                  There are no territories available to attach.
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenAttachTerritoryDialog(false);

              setSelectedTerritories([]);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              attachTerritories();
            }}
          >
            Attach
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={3}>
        <Card
          sx={{
            height: "100%",
            marginTop: "0px",
          }}
        >
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Territories</b>

                  <br />

                  <small>Restrict campaign activities to defined geographic territories.</small>
                </MDTypography>

                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedTerritories([]);

                    setOpenAttachTerritoryDialog(true);
                  }}
                >
                  <Icon
                    sx={{
                      fontWeight: "bold",
                    }}
                  >
                    add
                  </Icon>
                </MDButton>
              </MDBox>
            </MDBox>

            <MDBox mb={3}>
              <DataTable
                table={{
                  columns: campaignTerritoriesTableColumns,
                  rows: tableRows,
                }}
                isSorted
                entriesPerPage={false}
                showTotalEntries={false}
                buttons
                noEndBorder
                canSearch={false}
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default CampaignTerritoryCard;
